"use client"

import { useState } from "react"
import { FileText, X, ChevronUp, Scale, ShieldCheck, Cookie, Info } from "lucide-react"
import { Button } from "@/components/ui/button"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { ObfuscatedEmail } from "@/components/obfuscated-email"
import { triggerHaptic } from "@/hooks/use-platform"

/**
 * Legal Corner Button
 * 
 * Floating button in the bottom-right corner of the landing page.
 * Expands to show legal information links when clicked.
 */
export function LegalCornerButton() {
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpanded = () => {
    triggerHaptic('selection')
    setIsExpanded(!isExpanded)
  }

  return (
    <div className="fixed bottom-6 right-6 z-[60] pointer-events-auto flex flex-col items-end gap-3 group">
      {/* Expanded panel */}
      {isExpanded && (
        <div className="mb-2 bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl border border-white/20 dark:border-slate-800 rounded-[2.5rem] shadow-2xl p-6 min-w-[260px] max-w-[300px] z-[61] animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
                <Scale className="w-4 h-4 text-blue-500" />
              </div>
              <h3 className="text-sm font-black uppercase tracking-tight italic text-slate-900 dark:text-white">
                Legal Center
              </h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              onClick={() => setIsExpanded(false)}
              aria-label="Close legal menu"
            >
              <X className="h-4 w-4 text-slate-400" />
            </Button>
          </div>
          
          <div className="space-y-1">
            {[
              { label: "Terms of Use", href: "/legal", icon: Scale },
              { label: "Privacy Policy", href: "/legal", icon: ShieldCheck },
              { label: "Cookie Policy", href: "/legal", icon: Cookie },
              { label: "Disclaimer", href: "/legal", icon: Info },
            ].map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className="flex items-center gap-3 p-3 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all group/link"
                onClick={() => { triggerHaptic('impact'); setIsExpanded(false); }}
              >
                <link.icon className="w-3.5 h-3.5 transition-transform group-hover/link:scale-110" />
                <span className="italic">{link.label}</span>
              </Link>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
            <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Support Contact</p>
            <ObfuscatedEmail
              email="contact@spotstosurf.com"
              asLink
              className="block p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-[10px] font-bold text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all italic truncate"
            />
          </div>
        </div>
      )}

      {/* Floating Button - Matching PreferencesButton Style */}
      <button
        onClick={toggleExpanded}
        className={cn(
          "h-14 w-14 rounded-2xl flex items-center justify-center transition-all",
          "bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-white/20 dark:border-slate-800",
          "shadow-2xl shadow-blue-900/10 hover:scale-110 active:scale-95",
          isExpanded ? "bg-blue-600 text-white border-blue-600" : "text-slate-600 dark:text-slate-400"
        )}
        aria-label={isExpanded ? "Close legal menu" : "Open legal menu"}
      >
        {isExpanded ? (
          <X className="h-6 w-6 animate-in zoom-in duration-300" />
        ) : (
          <FileText className="h-6 w-6 group-hover:rotate-12 transition-transform duration-500" />
        )}
      </button>
    </div>
  )
}
